import React from 'react'
import { getFilenameFromUrl } from '../utils/fileHelpers'
import DocumentCard from './DocumentCard'
import VoiceMemoPlayer from './VoiceMemoPlayer'

/* SF Symbol play.fill */
const PlayOverlay = () => (
  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
    <div style={{ width: '35px', height: '35px', borderRadius: '50%', background: 'rgba(49, 49, 49, 0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="18" height="16" viewBox="0 0 7 11" fill="#fff">
        <path d="M0 9.22852V0.955078C0 0.310547 0.380859 0 0.832031 0C1.03125 0 1.23633 0.0585938 1.43555 0.169922L8.35547 4.21289C8.85938 4.50586 9.06445 4.72852 9.06445 5.0918C9.06445 5.44922 8.85938 5.67773 8.35547 5.9707L1.43555 10.0078C1.23633 10.1191 1.03125 10.1777 0.832031 10.1777C0.380859 10.1777 0 9.86719 0 9.22852Z"/>
      </svg>
    </div>
  </div>
)

function MediaRenderer({ log, onImageClick, onGalleryClick, onVideoClick, onDocOpen }) {
  if (!log.media_url && (!log.media_urls || !log.media_urls.length)) return null
  const urls = log.media_urls || [log.media_url]

  // Images (single or grouped)
  if (log.media_type?.startsWith('image/')) {
    if (urls.length > 1) return (
      <div onClick={() => onGalleryClick({ urls, index: 0, log })} style={{ display: 'grid', gridTemplateColumns: urls.length === 2 ? '1fr 1fr' : urls.length === 3 ? '1fr 1fr 1fr' : '1fr 1fr', gap: '4px', marginTop: '12px', cursor: 'pointer' }}>
        {urls.slice(0, 4).map((url, i) => (
          <div key={i} style={{ position: 'relative' }}>
            <img src={url} alt="Media" style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: 'var(--radius-md)' }} />
            {i === 3 && urls.length > 4 && <div style={{ position: 'absolute', inset: 0, background: 'var(--overlay-medium)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-primary)', fontSize: '24px', fontWeight: 'var(--weight-semibold)' }}>+{urls.length - 4}</div>}
          </div>
        ))}
      </div>
    )
    return (
      <div onClick={() => onImageClick({ url: urls[0], log })} style={{ marginTop: '12px', borderRadius: 'var(--radius-lg)', overflow: 'hidden', cursor: 'pointer' }}>
        <img src={urls[0]} alt="Media" style={{ width: '100%', height: '300px', objectFit: 'cover', display: 'block' }} />
      </div>
    )
  }

  // Videos (single or grouped — same grid as images)
  if (log.media_type?.startsWith('video/')) {
    if (urls.length > 1) return (
      <div style={{ display: 'grid', gridTemplateColumns: urls.length === 2 ? '1fr 1fr' : urls.length === 3 ? '1fr 1fr 1fr' : '1fr 1fr', gap: '4px', marginTop: '12px', cursor: 'pointer' }}>
        {urls.slice(0, 4).map((url, i) => (
          <div key={i} style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}
            onClick={() => onVideoClick({ url, log, urls, index: i })}>
            <video src={url} style={{ width: '100%', height: '150px', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} muted playsInline />
            <PlayOverlay />
            {i === 3 && urls.length > 4 && <div style={{ position: 'absolute', inset: 0, background: 'var(--overlay-medium)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-primary)', fontSize: '24px', fontWeight: 'var(--weight-semibold)', zIndex: 2 }}>+{urls.length - 4}</div>}
          </div>
        ))}
      </div>
    )
    return (
      <div onClick={() => onVideoClick({ url: urls[0], log, urls, index: 0 })} style={{ marginTop: '12px', borderRadius: 'var(--radius-lg)', overflow: 'hidden', cursor: 'pointer', position: 'relative', background: 'var(--color-bg-app)', height: '300px' }}>
        <video src={urls[0]} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} muted playsInline />
        <PlayOverlay />
      </div>
    )
  }

  // Audio / Voice memos
  if (log.media_type?.startsWith('audio/')) return (
    <VoiceMemoPlayer url={urls[0]} duration={0} />
  )

  // Documents
  const docFilename = log.content || getFilenameFromUrl(urls[0])
  return <DocumentCard url={urls[0]} filename={docFilename} mimeType={log.media_type} onOpen={() => onDocOpen({ url: urls[0], filename: docFilename, mimeType: log.media_type, log })} />
}

/* PERF: React.memo. Stable when log ref + handler refs are stable
 * (HomePage's stable proxy ensures the latter). This is the main
 * shield against feed-wide re-renders — keeps media grids, video
 * tiles, voice players, and doc cards from re-running their JSX
 * on every parent state change. */
export default React.memo(MediaRenderer)